import React from 'react';
import { isEmpty, isNil } from 'lodash';

import NotificationMessage from '~/components/ClaimPage/CliveApp/CliveNotifications/NotificationMessage';
import Text from '~/components/core/TextComponents/Text';
import cn from '~/Utils/cn';

import { StateChip } from '../../ActionStateChip';
import { ACTION_NEEDS_ATTENTION, QUESTION_STATE_CHIP } from '../../constants';
import { CONVERSATION_TEXTS } from '../../conversation/texts';
import { formatEntryTime, logEntryElementId } from '../../utils';
import JumpLink from '../JumpLink';
import QuestionEntry from '../QuestionEntry';
import type { LogEntryModel, MessageRef } from '../types';

import RunEnded from './RunEnded';

/**
 * The reply in Clive's colours, as its cards are; in a question's, while a question the reply raised waits on
 * the handler. Full literal class strings, so Tailwind's content scan keeps them.
 */
const REPLY_APPEARANCE = {
  clive: { card: 'border-l-purple-600', node: 'bg-purple-600', chip: 'bg-purple-100 text-purple-700' },
  waitingOnAnswer: { card: 'border-l-berry-600', node: 'bg-berry-600', chip: QUESTION_STATE_CHIP.pending },
};

interface MessageLinksProps {
  messages: MessageRef[];
  onJumpToMessage: (eventId: string) => void;
}

/** The messages a reply answers, each a link back to it: "to Dana Whitfield's message at 14:02 and …". */
const MessageLinks: React.FC<MessageLinksProps> = ({ messages, onJumpToMessage }) => (
  <>
    {CONVERSATION_TEXTS.TO}
    {messages.map(({ eventId, author, sentAt }, index) => {
      const label = CONVERSATION_TEXTS.MESSAGE_AT(author, formatEntryTime(sentAt));
      let joiner: string | null = null;
      if (index > 0)
        joiner = index === messages.length - 1 ? CONVERSATION_TEXTS.AND : CONVERSATION_TEXTS.LIST_SEPARATOR;

      return (
        <React.Fragment key={eventId}>
          {joiner}
          <JumpLink onClick={() => onJumpToMessage(eventId)}>{label}</JumpLink>
        </React.Fragment>
      );
    })}
  </>
);

interface RunFooterProps {
  entry: Extract<LogEntryModel, { kind: 'run_footer' }>;
  /** Refetches the asks after an answer to a question the reply raised. */
  onAskUpdate: () => Promise<void>;
  /** Takes the reader to one of the handler's messages, by its entry's event id. */
  onJumpToMessage: (eventId: string) => void;
  /** Shows a question's card in the activity panel, by its row's key. */
  onSelectActivityCard?: (key: string) => void;
}

/**
 * Clive's reply to the handler's messages, drawn where the run that gave it ended: the run's last word. The head
 * says which messages it answers, each a link back, and what Clive took them to be about; the body is the reply,
 * then each question Clive raised as its reply, the question's own card with its live options, so an answer here
 * is an answer to that question. The foot says how the run ended and keeps its summary behind a toggle, unless
 * the run ended on a card waiting for approval, which the log draws over this one with its summary.
 *
 * While a question the reply raised waits on the handler, the reply wears the question's colours and says so.
 */
const RunFooter: React.FC<RunFooterProps> = ({ entry, onAskUpdate, onJumpToMessage, onSelectActivityCard }) => {
  const { notification, ending, disposition, reply, foldedQuestions, messages } = entry;
  const isWaitingOnAnswer = foldedQuestions.some(({ state }) => !isNil(state) && ACTION_NEEDS_ATTENTION[state]);
  const appearance = isWaitingOnAnswer ? REPLY_APPEARANCE.waitingOnAnswer : REPLY_APPEARANCE.clive;
  // How the run ended, in the words its ending was published with.
  const chipLabel = isWaitingOnAnswer ? CONVERSATION_TEXTS.WAITING_ON_ANSWER : notification.title;

  return (
    <div className="flex gap-12">
      <div className="flex w-20 shrink-0 justify-center pt-20">
        <div className={cn('h-[12px] w-[12px] rounded-full', appearance.node)} aria-hidden />
      </div>

      <article
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-8 rounded-lg border border-l-2 border-solid border-grey-100 bg-slate-200 px-16 py-12',
          appearance.card
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-8 gap-y-4">
          {chipLabel ? <StateChip label={chipLabel} className={appearance.chip} /> : null}
          <Text
            variant={Text.VARIANTS.XS}
            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
            className="min-w-0 wrap-anywhere"
          >
            <span className="font-semibold text-slate-900">{CONVERSATION_TEXTS.REPLY}</span>
            {CONVERSATION_TEXTS.SEPARATOR}
            {formatEntryTime(notification.timestamp)}
            {isEmpty(messages) ? null : (
              <>
                {CONVERSATION_TEXTS.SEPARATOR}
                <MessageLinks messages={messages} onJumpToMessage={onJumpToMessage} />
              </>
            )}
            {CONVERSATION_TEXTS.SEPARATOR}
            {CONVERSATION_TEXTS.ABOUT}
            <span className="italic">{reply.messageSubject}</span>
          </Text>
        </div>

        {reply.text ? <NotificationMessage message={reply.text} /> : null}

        {foldedQuestions.map((question) => (
          // Where the activity panel takes the reader to the question, as it would to the question's own entry.
          <div key={question.notification.event_id} id={logEntryElementId(question.notification.event_id)}>
            <QuestionEntry
              notification={question.notification}
              ask={question.ask}
              state={question.state}
              onAskUpdate={onAskUpdate}
              onSelectActivityCard={onSelectActivityCard}
              inResponseTo={question.inResponseTo}
              onJumpToMessage={onJumpToMessage}
              isFolded
            />
          </div>
        ))}

        <RunEnded
          disposition={disposition}
          summary={ending.kind === 'action' ? null : (notification.message ?? null)}
        />
      </article>
    </div>
  );
};

export default RunFooter;
